/**
 * Un plato, un nombre (revisión de Alex del 14 oct 2026).
 *
 * El nombre que se ve de un plato es SIEMPRE el de su línea en la carta
 * (MenuItem.name), si la tiene; la receta solo pone el nombre cuando el plato
 * no está en la carta o es una preparación. Antes la lista y la ficha decían
 * «Croquetas caseras (6 ud)» (carta) y «Se usa en», la ficha técnica y el PDF
 * decían «Croquetas caseras (12 ud)» (receta).
 *
 * Si hay varias líneas para la misma receta (platos viejos «— Uber Eats»
 * clavados a una app), manda la de la carta general (sin app) y activa; si
 * solo queda la de la app, sin el sufijo « — Uber Eats».
 */
import type { MenuItem, Recipe } from './types';

type RecetaConNombre = Pick<Recipe, 'recipeId' | 'name'>;
type LineaCarta = Pick<MenuItem, 'recipeId' | 'name'> & Partial<Pick<MenuItem, 'platformId' | 'active'>>;

/** Línea de carta que da nombre al plato, o `undefined` si no está en la carta. */
export function lineaDelPlato<T extends LineaCarta>(recipeId: string, menuItems: readonly T[] | undefined): T | undefined {
  if (!menuItems?.length) return undefined;
  const suyas = menuItems.filter((m) => m.recipeId === recipeId && m.name?.trim());
  if (!suyas.length) return undefined;
  const puntos = (m: T) => (m.platformId ? 0 : 2) + (m.active === false ? 0 : 1);
  return suyas.reduce((mejor, m) => (puntos(m) > puntos(mejor) ? m : mejor));
}

/** Quita « — Uber Eats» (o « - Glovo») del nombre de un plato clavado a una app. */
function sinSufijoDeApp(nombre: string): string {
  const limpio = nombre.replace(/\s+[—–-]\s+[^—–-]+$/u, '').trim();
  return limpio || nombre;
}

/** El nombre que se enseña de una receta: el del plato en la carta si existe. */
export function nombreDelPlato(recipe: RecetaConNombre, menuItems: readonly LineaCarta[] | undefined): string {
  const linea = lineaDelPlato(recipe.recipeId, menuItems);
  if (!linea) return recipe.name;
  return linea.platformId ? sinSufijoDeApp(linea.name.trim()) : linea.name.trim();
}

/** Mapa recipeId → nombre visible, para pantallas que pintan muchas recetas. */
export function nombresDePlatos(recipes: readonly RecetaConNombre[], menuItems: readonly LineaCarta[] | undefined): Map<string, string> {
  return new Map(recipes.map((r) => [r.recipeId, nombreDelPlato(r, menuItems)]));
}
